import axios from 'axios';

import { AUTH_TOKEN, LANG, REFRESH_TOKEN, getFromLocalStorage } from 'utils/localStorage';

import { EXPIRED_TOKEN_MESSAGE, INVALID_TOKEN_MESSAGE } from 'config/errors';

import { store } from 'store';
import { logout, setAuthInfo } from 'store/actions/auth.actions';

let { __DEV__, API_URL = 'https://admin.joy-jet.com' } = process.env;
if (API_URL.includes('//joy-jet.com')) {
  API_URL = API_URL.replace('//joy-jet.com', '//admin.joy-jet.com');
}

let _isAlreadyRefreshing = false;
let subscribers = [];

const baseURL = __DEV__ ? '/api/admin' : `${API_URL}/admin`;

const instance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: '*/*',
  },
});

const refreshInstance = axios.create({ baseURL });

function onAccessTokenFetched(access_token) {
  subscribers = subscribers.filter((callback) => callback(access_token));
}

function onRefreshTokenError() {
  subscribers = subscribers.filter((callback) => callback(''));
}

function addSubscriber(callback) {
  subscribers.push(callback);
}

instance.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) {
      const token = getFromLocalStorage(AUTH_TOKEN);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    const lang = getFromLocalStorage(LANG);
    if (lang) {
      config.headers['Accept-Language'] = lang;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const { response, config } = error;
    const {
      data: { code, message },
    } = response;

    if (!config.headers.Authorization || code !== 401) {
      // return regular error
      return Promise.reject(error);
    }
    if (message === INVALID_TOKEN_MESSAGE) {
      store.dispatch(logout());
      return Promise.reject(INVALID_TOKEN_MESSAGE);
    }

    if (message === EXPIRED_TOKEN_MESSAGE) {
      refreshToken();
      const { config: originalRequest = {} } = error;
      return new Promise((resolve, reject) => {
        addSubscriber((newAccessToken) => {
          if (!newAccessToken) {
            return reject({ error: 'Failed to refresh token' });
          }
          originalRequest.headers.Authorization = 'Bearer ' + newAccessToken;
          resolve(instance.request(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  },
);

const refreshToken = () => {
  if (!_isAlreadyRefreshing) {
    _isAlreadyRefreshing = true;
    const refreshToken = getFromLocalStorage(REFRESH_TOKEN);
    refreshInstance
      .post('/token/refresh', { refresh_token: refreshToken })
      .then((refreshResponse) => {
        const { data = {} } = refreshResponse;
        const { token, refresh_token: refreshToken } = data;
        _isAlreadyRefreshing = false;
        onAccessTokenFetched(token);
        store.dispatch(setAuthInfo({ token, refreshToken }));
      })
      .catch(() => {
        _isAlreadyRefreshing = false;
        store.dispatch(logout());
        onRefreshTokenError();
      });
  }
};

export { API_URL };

export default instance;
